/**
 * 点击外部指令
 *
 * 用于检测点击元素外部的行为
 *
 * @module shared/directives
 */

import type { App, Directive, DirectiveBinding } from "vue";

type ClickOutsideHandler = (event: MouseEvent) => void;

interface ClickOutsideElement extends HTMLElement {
  __clickOutsideHandler__?: (event: MouseEvent) => void;
}

/**
 * v-click-outside 指令
 *
 * 使用方法：
 * ```html
 * <div v-click-outside="handleClickOutside">...</div>
 * ```
 */
export const vClickOutside: Directive = {
  mounted(
    el: ClickOutsideElement,
    binding: DirectiveBinding<ClickOutsideHandler>,
  ) {
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;

      // 检查点击是否在元素外部
      if (!el.contains(target) && el !== target) {
        binding.value(event);
      }
    };

    // 保存handler引用以便卸载时移除
    el.__clickOutsideHandler__ = handler;

    // 添加事件监听
    document.addEventListener("click", handler, true);
  },

  unmounted(el: ClickOutsideElement) {
    // 移除事件监听
    if (el.__clickOutsideHandler__) {
      document.removeEventListener("click", el.__clickOutsideHandler__, true);
      delete el.__clickOutsideHandler__;
    }
  },
};

/**
 * 注册指令到 Vue 应用
 */
export function registerClickOutside(app: App) {
  app.directive("click-outside", vClickOutside);
}

export default vClickOutside;
